import React from 'react';
import { 
  Box, Typography, Grid, Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useOptionChainData } from '../hooks/useOptionChainData';
import OptionListCard from './OptionListCard';
import { ListRowSkeleton } from '../../../components/DashboardSkeletons';
import { formatSmartOptionExpiry } from '../../../utils/expiryUtils';

const OptionChainWidget = ({ selectedIndex }) => {
  const theme = useTheme();
  const {
    spotPrice,
    loading,
    optionChain,
    expiries,
    selectedExpiry,
    setSelectedExpiry
  } = useOptionChainData(selectedIndex);

  // Filter for exactly 2 strikes above and 2 below ATM
  const filteredData = React.useMemo(() => {
    if (!optionChain || optionChain.length === 0) return [];

    // Sort by strike ascending first to ensure consistent base
    const sorted = [...optionChain].sort((a, b) => a.strike_price - b.strike_price);

    let atmIndex = 0;
    let minDiff = Infinity;
    
    sorted.forEach((row, i) => {
      const diff = Math.abs(row.strike_price - spotPrice);
      if (diff < minDiff) {
        minDiff = diff;
        atmIndex = i;
      }
    });

    // We want 2 ITM, 1 ATM, 2 OTM -> Total 5
    // Slice centered on ATM
    const start = Math.max(0, Math.min(sorted.length - 5, atmIndex - 2));
    const end = Math.min(sorted.length, start + 5);
    
    return sorted.slice(start, end);
  }, [optionChain, spotPrice]);

  // For the Put side, the user wants the highest strike at the top (reversed order)
  const callData = filteredData;
  const putData = [...filteredData].sort((a, b) => b.strike_price - a.strike_price);

  if (loading && optionChain.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={800} color="text.primary">OPTIONS</Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <ListRowSkeleton count={5} rowHeight={56} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <ListRowSkeleton count={5} rowHeight={56} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Top Header Row for Spot Price and Expiry */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        gap: 2
      }}>
        <Typography variant="subtitle2" fontWeight={800} color="text.primary">
          OPTIONS
        </Typography>

        {selectedExpiry && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
              EXPIRY:
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
              {formatSmartOptionExpiry(selectedExpiry)}
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Split Card Layout */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <OptionListCard 
            side="call" 
            data={callData} 
            spotPrice={spotPrice} 
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <OptionListCard 
            side="put" 
            data={putData} 
            spotPrice={spotPrice} 
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default OptionChainWidget;
